import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { Form, Button, Switch, Modal, Row, Col } from 'antd';

import { useWindowSize } from '@/hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import CustomerGeneralInfoForm from '@/components/Customers/CustomerGeneralInfoForm';
import { SaveButton } from '@/components/_common/Buttons';
import {
  useDeleteCustomer,
  useUpdateCustomerShowLGPD,
} from '@/core/domain/people/Customers/Customers.hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useCustomerFormContext } from '@/components/Customers/CustomerFormProvider';
import { PageProps } from '@/types/PageProps';
import { useHistory } from 'react-router-dom';
import Authorities from '@/types/Authority';
import CustomerFooter from '@/components/Customers/CustomerRepeatedButtons';
import { CustomerFormValues } from '@/core/domain/people/Customers/Customers.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { UserType } from '@/core/domain/yCarsAuth/User/User.types';
import SentEmailValidateCodeYcarsAlert from '@/components/_common/SentEmailValidateCodeYcarsAlert';
import { AuthorityClientArea } from '@/types/Authority/Authority.ClientArea';
import CustomerCategoryTag from '@/components/Customers/CustomerCategoryTag';
import CustomerDefaulterAlert from '@/components/Movements/WorkOrders/WorkOrderForm/CustomerDefaulterAlert';

const CustomerGeneralInfoPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const [form] = Form.useForm<CustomerFormValues>();
  const [reminder, setReminder] = useState<boolean>();
  const history = useHistory();

  const {
    isLoading,
    forms,
    customerId,
    disabled,
    handleActive,
    activeCustomer,
    setActiveCustomer,
    loadActive,
    setLoadActive,
    customer,
    customerDetailsLoading,
  } = useCustomerFormContext();

  const { setParamLabel } = useBreadcrumbs();

  const removeMutation = useDeleteCustomer();
  const remindShowLgpd = useUpdateCustomerShowLGPD();

  useEffect(() => {
    if (forms.customerGeneral) {
      form.setFieldsValue({
        ...forms.customerGeneral,
      });
    } else if (customerId && !customerDetailsLoading && customer) {
      setActiveCustomer(customer.active);
      setLoadActive(false);

      setReminder(customer.showLGPD);

      const obj: CustomerFormValues = {
        ...customer,
        enableLGPD:
          typeof customer.enableLGPD === 'boolean'
            ? customer.enableLGPD
            : undefined,
        birthDate: customer.birthDate ? moment(customer.birthDate) : undefined,
        creation: customer.creation ? moment(customer.creation) : undefined,
        creditLimit: customer.creditLimit ? customer.creditLimit : 0,
        recommendation: { id: customer.recommendationId },
      };

      form.setFieldsValue(obj);

      setParamLabel(':customerId', customer.name);
    }
  }, [customer, customerDetailsLoading]);

  const handleRemind = async () => {
    try {
      await remindShowLgpd
        .mutateAsync(Number(customerId))
        .then(() => setReminder(!reminder));
    } catch {}
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      form.submit();
    } catch {}
  };

  const handleRemove = async () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'customers.notifications.remove.confirm.content',
      })} ${customer?.name}`,
      onOk: async () => {
        try {
          await removeMutation
            .mutateAsync(Number(customerId))
            .then(() => history.push('/app/customers/'));
        } catch {}
      },
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={`${customerId}`}
        basePath={`/app/customers/${customerId}`}
        extra={
          <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={loadActive}
              checked={activeCustomer}
              onChange={handleActive}
              disabled={
                !hasPermissions([Authorities.REGISTER_CUSTOMER_ACTIVE_INACTIVE])
              }
            />

            <Button
              block
              danger
              onClick={handleRemove}
              loading={removeMutation.isLoading}
              disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_DELETE])}
            >
              <FormattedMessage id="commons.entity.remove" />
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={
                disabled ||
                !hasPermissions([Authorities.REGISTER_CUSTOMER_EDIT])
              }
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Row gutter={[16, 16]}>
          {hasPermissions([AuthorityClientArea.CLIENT_AREA]) &&
            customer?.cpfCnpj && (
              <Col span={24} md={18}>
                <SentEmailValidateCodeYcarsAlert
                  ycarsStatus={customer?.ycarsStatus}
                  user={{
                    cpfCnpj: customer?.cpfCnpj,
                    id: Number(customerId),
                    name: customer?.name,
                    type: UserType.customer,
                    rentalCompany: customer.rentalCompany,
                  }}
                />
              </Col>
            )}

          <Col span={24} md={6} style={{ marginBottom: 24 }}>
            <CustomerDefaulterAlert customerId={Number(customerId)} />
          </Col>
        </Row>

        <CustomerCategoryTag />

        <CustomerGeneralInfoForm
          form={form}
          showTitle={false}
          personType={forms.customerGeneral?.personType}
          remind={reminder}
          reminderLoad={remindShowLgpd.isLoading}
          setReminderShow={handleRemind}
          isEdit
        />

        <CustomerFooter customerId={Number(customerId)} disabled={disabled} />
      </DefaultRouteTabs>
    </>
  );
};

export default CustomerGeneralInfoPage;
